import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/authStore';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/Button';

const navItems = [
  { to: '/', key: 'web.nav.dashboard', end: true },
  { to: '/payments', key: 'web.nav.payments' },
  { to: '/utilities', key: 'web.nav.utilities' },
  { to: '/maintenance', key: 'web.nav.maintenance' },
  { to: '/contacts', key: 'web.nav.contacts' },
  { to: '/inspections', key: 'web.nav.inspections' },
  { to: '/acts', key: 'web.nav.acts' },
  { to: '/applications', key: 'web.nav.applications' },
  { to: '/trust-passport', key: 'web.nav.trust_passport' },
  { to: '/chat', key: 'web.nav.chat' },
  { to: '/search', key: 'web.nav.search' },
  { to: '/profile', key: 'web.nav.profile' },
] as const;

function Breadcrumbs() {
  const { t } = useTranslation();
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  return (
    <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-slate-500">
      <NavLink to="/" className="hover:text-brand">
        {t('web.breadcrumbs.home')}
      </NavLink>
      {segments.map((s, i) => (
        <span key={i} className="flex items-center gap-1">
          <span>/</span>
          <span className="capitalize">{s}</span>
        </span>
      ))}
    </nav>
  );
}

export function TenantLayout() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-64 shrink-0 flex-col bg-white md:flex">
        <div className="flex h-16 items-center border-b border-slate-200 px-5 text-lg font-semibold text-brand">
          Monthly
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              className={({ isActive }) =>
                `block rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? 'bg-brand-50 text-brand-600'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <Button variant="ghost" className="w-full" onClick={() => void logout()}>
            {t('web.nav.logout')}
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6">
          <div className="text-sm text-slate-700">
            {user ? t('web.header.greeting', { name: user.name }) : null}
          </div>
          <LanguageSwitcher />
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
